import cn from 'classnames'
import {FC, ReactNode} from 'react'

import {Badge} from '@/components/common//badges/Badge'
import {HtmlElementType, Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {FormTooltip} from '@/components/common/form/tooltip/FormTooltip'
import {LocalizedNumberFormat} from '@/components/common/LocalizedNumberFormat'
import {useCommonContent} from '@/content/common'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {Currency} from '@/utils/currency'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'
import {CardContentButtonType} from '@/utils/user-portal/types'

import {CardContentButton} from './CardContentButton'

type CardContentProps = {
  icon?: ReactNode
  title?: string
  titleVariant?: TextVariant
  titleElement?: HtmlElementType
  titleFontWeight?: TextFontWeight
  description?: string | ReactNode
  descriptionVariant?: TextVariant
  price?: number
  priceTooltip?: ReactNode
  priceNote?: string
  currency?: Currency
  bulletPointsTitle?: string
  bulletPointsList?: string[]
  primaryButton?: CardContentButtonType
  secondaryButton?: CardContentButtonType
  isFullHeight?: boolean
  badgeText?: string
}

export const CardContent: FC<React.PropsWithChildren<CardContentProps>> = ({
  icon,
  title,
  titleVariant = TextVariant.h6,
  titleElement = 'h4',
  titleFontWeight,
  description,
  descriptionVariant = TextVariant.text14,
  price,
  priceTooltip,
  priceNote,
  currency,
  bulletPointsTitle,
  bulletPointsList,
  primaryButton,
  secondaryButton,
  isFullHeight = true,
  badgeText,
  children,
}) => {
  const {t} = useTranslation()
  const {freeTitle} = useCommonContent()
  const hasButtonVariant =
    primaryButton?.variant !== undefined || secondaryButton?.variant !== undefined

  return (
    <>
      <div className="wrapper">
        <div>
          {icon && <div className="icon">{icon}</div>}
          {badgeText && (
            <div className="icon">
              <Badge title={badgeText} />
            </div>
          )}
          {title && (
            <Text variant={titleVariant} element={titleElement} fontWeight={titleFontWeight}>
              {title}
            </Text>
          )}
          {(!!price || price === 0) && (
            <div className="price">
              <Text variant={TextVariant.h4} element="p">
                {price > 0 ? (
                  <LocalizedNumberFormat value={price} currency={currency} type="currency" />
                ) : (
                  t(freeTitle)
                )}
              </Text>
              {priceNote && (
                <div className="note">
                  <Text element="small" color={theme.palette.text.light}>
                    /{priceNote}
                  </Text>
                </div>
              )}
              {priceTooltip && (
                <div className="tooltip">
                  <FormTooltip tooltipText={priceTooltip} />
                </div>
              )}
            </div>
          )}
          {description && (
            <div className="description">
              <Text variant={descriptionVariant} color={theme.palette.text.light}>
                {description}
              </Text>
            </div>
          )}
          {bulletPointsTitle && bulletPointsList && (
            <div className="description">
              <Text variant={TextVariant.text14} color={theme.palette.text.light}>
                {bulletPointsTitle}
              </Text>
              {bulletPointsList.map((item) => (
                <div key={item} className="bullet-point">
                  <Text variant={TextVariant.text14} color={theme.palette.text.light}>
                    {t(item)}
                  </Text>
                </div>
              ))}
            </div>
          )}
          {children}
        </div>
        {(primaryButton || secondaryButton) && (
          <div
            className={cn(
              'buttons',
              (primaryButton?.isFullWidth || secondaryButton?.isFullWidth) && 'full-width',
            )}
          >
            {primaryButton && (
              <div className="button">
                <CardContentButton button={primaryButton} />
              </div>
            )}
            {secondaryButton && (
              <div className="button">
                <CardContentButton button={secondaryButton} />
              </div>
            )}
          </div>
        )}
      </div>

      <style jsx>{`
        .wrapper {
          display: flex;
          flex-direction: column;
          justify-content: ${isFullHeight ? 'space-between' : 'flex-start'};
          height: 100%;
        }
        .icon {
          display: flex;
          margin-bottom: ${theme.spacing[2.5]};
        }
        .price {
          display: flex;
          align-items: center;
          margin-top: ${theme.spacing[1]};
        }
        .tooltip {
          display: flex;
          margin-left: ${theme.spacing[1]};
        }
        .description {
          margin-top: ${theme.spacing[2.5]};
        }
        .description + .description {
          margin-top: ${theme.spacing[3]};
        }
        .bullet-point {
          display: list-item;
          margin-left: ${theme.spacing[4]};
        }
        .buttons {
          display: flex;
          margin: ${hasButtonVariant ? theme.spacing[4] : theme.spacing[1]} -${theme.spacing[1]} -${theme
              .spacing[1]};
        }
        .button {
          margin: ${theme.spacing[1]};
        }
        .full-width {
          display: block;
        }
        .full-width .button + .button {
          margin-top: ${theme.spacing[2]};
        }
        .note {
          margin-top: -${theme.spacing[2]};
          margin-left: ${theme.spacing[1]};
        }
        @media ${MOBILE_SCREENSIZE} {
          .buttons {
            flex-wrap: wrap;
          }
          .description {
            margin-top: ${hasButtonVariant ? theme.spacing[3.5] : theme.spacing[2]};
          }
        }
      `}</style>
    </>
  )
}
